/**
 * api/assignments.js
 *
 * REST API calls for form assignment settings.
 * Base: /wp-json/wpbean-pgs/v1/settings
 */

import apiFetch from "@wordpress/api-fetch";

const BASE = "/wpbean-pgs/v1/settings";

/**
 * Get current assignments for a form.
 * @param {number} id
 * @returns {Promise<{ categories: number[], products: number[] }>}
 */
export const getAssignments = (id) =>
    apiFetch({ path: `${BASE}/${id}/assignments` });

/**
 * Save (full replace) assignments for a settings rule.
 * @param {number}   id
 * @param {string}   applyTo     'all' | 'selected'
 * @param {number[]} categories  WooCommerce product_cat term IDs
 * @param {number[]} products    WP post IDs
 */
export const saveAssignments = (id, applyTo, categories, products) =>
    apiFetch({
        path: `${BASE}/${id}/assignments`,
        method: "PUT",
        data: { apply_to: applyTo, categories, products },
    });

/**
 * Fetch all WooCommerce product categories (flat list with parent refs).
 * @returns {Promise<Array<{ id: number, name: string, parent: number, count: number }>>}
 */
export const getWcCategories = () =>
    apiFetch({ path: `${BASE}/wc/categories` });

/**
 * Search WooCommerce products by text query.
 * @param {{ search?: string, page?: number, per_page?: number }} params
 * @returns {Promise<{ items: Array<{ id: number, name: string, sku: string }>, total: number }>}
 */
export const searchWcProducts = ({
    search = "",
    page = 1,
    per_page = 20,
} = {}) => {
    const query = new URLSearchParams({
        page,
        per_page,
        ...(search && { search }),
    }).toString();
    return apiFetch({ path: `${BASE}/wc/products?${query}` });
};

/**
 * Fetch specific products by their exact post IDs.
 * Used on page load to resolve saved assignment IDs into full {id, name, sku} objects.
 * Returns products in the same order as the IDs array.
 *
 * @param {number[]} ids
 * @returns {Promise<{ items: Array<{ id: number, name: string, sku: string }>, total: number }>}
 */
export const getWcProductsByIds = (ids) => {
    if (!ids?.length) return Promise.resolve({ items: [], total: 0 });
    return apiFetch({ path: `${BASE}/wc/products?ids=${ids.join(",")}` });
};
